import { formatDisplayValue, humanizeKey, shouldSkipKey } from '../CognitoFormViewer/helpers'

export const PREVIEW_ATTRIBUTE_COUNT = 5

const previewField = index => `preview_attr_${index}`

const tryParseJson = value => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null
  try {
    return JSON.parse(trimmed)
  } catch (e) {
    return null
  }
}

const formatObjectPreview = obj => {
  if (obj.FirstAndLast) return String(obj.FirstAndLast)
  if (obj.FullName) return String(obj.FullName)
  if (obj.Name) return String(obj.Name)

  const first = obj.First || obj.FirstName || obj.firstname
  const last = obj.Last || obj.LastName || obj.surname || obj.Surname
  if (first || last) return [first, last].filter(Boolean).join(' ')

  return Object.values(obj)
    .filter(value => typeof value === 'string' && value)
    .slice(0, 2)
    .join(', ')
}

export const formatPreviewValue = value => {
  const parsed = tryParseJson(value)
  if (Array.isArray(parsed)) {
    return parsed
      .map(item => (item && typeof item === 'object' ? formatObjectPreview(item) : formatDisplayValue(item)))
      .filter(Boolean)
      .slice(0, 3)
      .join(', ')
  }
  if (parsed && typeof parsed === 'object') {
    return formatObjectPreview(parsed)
  }
  return formatDisplayValue(value)
}

export const getParsedData = row => {
  const parsed = row?.parsed_data
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
  return parsed
}

export const formatPreviewLabel = key => {
  const leaf = String(key).split('.').pop()
  return humanizeKey(leaf) || key
}

export const getPreviewAttributeKeys = (rows, limit = PREVIEW_ATTRIBUTE_COUNT) => {
  let source = {}

  for (const row of rows) {
    const parsed = getParsedData(row)
    if (Object.keys(parsed).length > Object.keys(source).length) {
      source = parsed
    }
  }

  const keys = Object.keys(source).filter(key => {
    const leaf = key.split('.').pop()
    return !shouldSkipKey(leaf, source[key]) && (source[key] === null || typeof source[key] !== 'object')
  })

  const withValues = keys.filter(key => rows.some(row => formatPreviewValue(getParsedData(row)[key]) !== ''))

  return (withValues.length ? withValues : keys).slice(0, limit)
}

export const buildPreviewColumns = keys => {
  const usedNames = new Set()

  return keys.map((key, index) => {
    let name = formatPreviewLabel(key) || key
    if (usedNames.has(name)) {
      name = `${name} (${index + 1})`
    }
    usedNames.add(name)

    return {
      name,
      field: previewField(index),
      searchInputType: 'textInput',
      cellType: 'text',
    }
  })
}

export const withPreviewAttributes = (row, keys) => {
  const parsed = getParsedData(row)
  const preview = {}

  keys.forEach((key, index) => {
    preview[previewField(index)] = formatPreviewValue(parsed[key])
  })

  return { ...row, ...preview }
}
